import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import React, { useEffect, useMemo, useState } from 'react';

import { isAdmin, useAuthenticationService } from '@/services/authentication/authentication-service';
import { useVariable } from '@/services/backstage/BackstageProvider';
import { handleError } from '@/services/gql/config/URQLProvider';
import { useGetSupportedHolidayRegionsQuery } from '@/services/gql/graphql.generated';
import { AdminSettings } from '@/types/dataManagement.types';
import strings from '../tenantSettings.translations';
import { OptionItem } from './DynamicCellRenderer';
import { GenericSettingsGrid, SettingsGridRow } from './GenericSettingsGrid';
import { isRowDataDirty } from './settingsGrid.utils';

const ESS_BEHAVIOUR_KEYS = {
  allowSelfEditEmail: 'essBehaviour.allowSelfEditEmail',
  allowSelfEditPhoneNumber: 'essBehaviour.allowSelfEditPhoneNumber'
} as const;

const OPTIMISER_SOLVER_KEYS = {
  enabledSolvers: 'optimiser.enabledSolvers',
  enableGeneticAlgorithm: 'optimiser.enableGeneticAlgorithm',
  enableCpSat: 'optimiser.enableCpSat',
  showEvaluation: 'optimiser.showEvaluation'
} as const;

type AdminSettingsProperties = {
  [ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]?: boolean;
  [ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enabledSolvers]?: 'geneticAlgorithm' | 'cpSat' | 'both';
  [OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]?: boolean;
  [OPTIMISER_SOLVER_KEYS.enableCpSat]?: boolean;
  [OPTIMISER_SOLVER_KEYS.showEvaluation]?: boolean;
  [key: string]: unknown;
};

type SettingsGridRowDef = SettingsGridRow & { superAdminOnly?: boolean };

const getOptimiserEnabledSolvers = (properties: AdminSettingsProperties): 'geneticAlgorithm' | 'cpSat' | 'both' => {
  const enabledSolvers = properties[OPTIMISER_SOLVER_KEYS.enabledSolvers];

  if (enabledSolvers === 'geneticAlgorithm' || enabledSolvers === 'cpSat' || enabledSolvers === 'both') {
    return enabledSolvers;
  }

  if (
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm]) &&
    Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])
  ) {
    return 'both';
  }

  if (Boolean(properties[OPTIMISER_SOLVER_KEYS.enableCpSat])) {
    return 'cpSat';
  }

  return 'geneticAlgorithm';
};

const parseProperties = (properties?: string | null): AdminSettingsProperties => {
  if (!properties) {
    return {};
  }

  try {
    const parsed = JSON.parse(properties);
    return parsed && typeof parsed === 'object' ? (parsed as AdminSettingsProperties) : {};
  } catch {
    return {};
  }
};

// ---------------------------------------
// Register Ag-Grid Community Modules
// ---------------------------------------
ModuleRegistry.registerModules([AllCommunityModule]);

// ---------------------------------------
// Map API response to rowData
// ---------------------------------------
const mapOrganisationSettingsToRowData = (
  data: AdminSettings,
  holidayRegionOptions: OptionItem[],
  superAdmin: boolean
): SettingsGridRow[] => {
  const properties = parseProperties(data.properties);

  const rows: SettingsGridRowDef[] = [
    {
      id: 'section-leave-type',
      label: strings.admin.sectionLeaveType,
      description: strings.admin.sectionLeaveTypeDescription,
      isSection: true,
      superAdminOnly: true
    },
    {
      id: 'leaveTypeTier',
      label: strings.admin.leaveTypeTier,
      description: strings.admin.leaveTypeTierDescription,
      value: data.leaveTypeTier ?? undefined,
      type: 'select',
      options: [
        { value: 0, label: strings.admin.standard },
        { value: 1, label: strings.admin.advanced }
      ],
      isSection: false,
      superAdminOnly: true
    },
    {
      id: 'section-contract',
      label: strings.admin.contract.title,
      description: strings.admin.contract.description,
      isSection: true,
      superAdminOnly: true
    },
    {
      id: 'contractTier',
      label: strings.admin.contract.contractTier,
      description: strings.admin.contract.contractTierDescription,
      value: data.contractTier ?? undefined,
      type: 'select',
      options: [
        { value: 0, label: strings.admin.standard },
        { value: 1, label: strings.admin.advanced }
      ],
      isSection: false,
      superAdminOnly: true
    },
    {
      id: 'section-working-time-rules',
      label: strings.admin.sectionWorkingTimeRules,
      description: strings.admin.sectionWorkingTimeRulesDescription,
      isSection: true
    },
    {
      id: 'minMinutesBeforeBreak',
      label: strings.admin.minMinutesBeforeBreakLabel,
      description: strings.admin.minMinutesBeforeBreakDescription,
      value: data.minMinutesBeforeBreak,
      type: 'timespan',
      range: { start: '01:00', end: '03:00', step: 15, displayHHmm: false },
      isSection: false
    },
    {
      id: 'minMinutesAfterBreak',
      label: strings.admin.minMinutesAfterBreakLabel,
      description: strings.admin.minMinutesAfterBreakDescription,
      value: data.minMinutesAfterBreak,
      type: 'timespan',
      range: { start: '01:00', end: '03:00', step: 15, displayHHmm: false },
      isSection: false
    },
    {
      id: 'shortestBreak',
      label: strings.admin.shortestBreakLabel,
      description: strings.admin.shortestBreakDescription,
      value: data.shortestBreak,
      type: 'timespan',
      range: { start: '00:15', end: '01:00', step: 15, displayHHmm: false },
      isSection: false
    },
    {
      id: 'section-ess-behaviour',
      label: strings.admin.sectionEssBehaviour,
      description: strings.admin.sectionEssBehaviourDescription,
      isSection: true
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditEmail,
      label: strings.admin.allowEssEditEmailLabel,
      description: strings.admin.allowEssEditEmailDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail]),
      type: 'boolean',
      isSection: false
    },
    {
      id: ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber,
      label: strings.admin.allowEssEditPhoneNumberLabel,
      description: strings.admin.allowEssEditPhoneNumberDescription,
      value: Boolean(properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber]),
      type: 'boolean',
      isSection: false
    },
    {
      id: 'section-holiday-settings',
      label: strings.admin.sectionHolidaySettings,
      description: strings.admin.sectionHolidaySettingsDescription,
      isSection: true
    },
    {
      id: 'defaultHolidayRegionCode',
      label: strings.admin.defaultHolidayRegionCodeLabel,
      description: strings.admin.defaultHolidayRegionCodeDescription,
      value: data.defaultHolidayRegionCode ?? undefined,
      type: 'select',
      options: holidayRegionOptions,
      isSection: false
    },
    {
      id: 'section-optimisation-settings',
      label: strings.admin.sectionOptimisationSettings,
      description: strings.admin.sectionOptimisationSettingsDescription,
      isSection: true
    },
    {
      id: OPTIMISER_SOLVER_KEYS.enabledSolvers,
      label: strings.admin.enabledSolversLabel,
      description: strings.admin.enabledSolversDescription,
      value: getOptimiserEnabledSolvers(properties),
      type: 'select',
      options: [
        { value: 'geneticAlgorithm', label: strings.admin.enabledSolversGeneticAlgorithm },
        { value: 'cpSat', label: strings.admin.enabledSolversCpSat },
        { value: 'both', label: strings.admin.enabledSolversBoth }
      ],
      isSection: false
    },
    {
      id: OPTIMISER_SOLVER_KEYS.showEvaluation,
      label: strings.admin.optimisationEvaluationLabel,
      description: strings.admin.optimisationEvaluationDescription,
      value: Boolean(properties[OPTIMISER_SOLVER_KEYS.showEvaluation]),
      type: 'boolean',
      isSection: false
    }
  ];

  return rows.filter(({ superAdminOnly }) => !superAdminOnly || superAdmin);
};

// ---------------------------------------
// Map back to API model
// ---------------------------------------
const mapFromRowData = (snapshot: AdminSettings, rowData: SettingsGridRow[]): AdminSettings => {
  const getValue = (id: string) => rowData.find(r => r.id === id)?.value as string | number | undefined;

  const leaveTypeTier = getValue('leaveTypeTier') as number;
  const contractTier = getValue('contractTier') as number;
  const minMinutesBeforeBreak = getValue('minMinutesBeforeBreak') as string;
  const minMinutesAfterBreak = getValue('minMinutesAfterBreak') as string;
  const shortestBreak = getValue('shortestBreak') as string;
  const defaultHolidayRegionCode = getValue('defaultHolidayRegionCode') as string;
  const allowSelfEditEmail = rowData.find(r => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditEmail)?.value;
  const allowSelfEditPhoneNumber = rowData.find(r => r.id === ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber)?.value;
  const enabledSolvers = rowData.find(r => r.id === OPTIMISER_SOLVER_KEYS.enabledSolvers)?.value;
  const showEvaluation = rowData.find(r => r.id === OPTIMISER_SOLVER_KEYS.showEvaluation)?.value;

  if (
    minMinutesBeforeBreak === undefined ||
    minMinutesAfterBreak === undefined ||
    shortestBreak === undefined ||
    defaultHolidayRegionCode === undefined ||
    typeof allowSelfEditEmail !== 'boolean' ||
    typeof allowSelfEditPhoneNumber !== 'boolean' ||
    typeof showEvaluation !== 'boolean' ||
    (enabledSolvers !== 'geneticAlgorithm' && enabledSolvers !== 'cpSat' && enabledSolvers !== 'both')
  ) {
    return snapshot;
  }

  const properties = parseProperties(snapshot.properties);
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditEmail] = allowSelfEditEmail;
  properties[ESS_BEHAVIOUR_KEYS.allowSelfEditPhoneNumber] = allowSelfEditPhoneNumber;
  properties[OPTIMISER_SOLVER_KEYS.enabledSolvers] = enabledSolvers;
  properties[OPTIMISER_SOLVER_KEYS.showEvaluation] = showEvaluation;
  delete properties[OPTIMISER_SOLVER_KEYS.enableGeneticAlgorithm];
  delete properties[OPTIMISER_SOLVER_KEYS.enableCpSat];

  return {
    ...snapshot,
    ...(leaveTypeTier !== undefined && { leaveTypeTier }),
    ...(contractTier !== undefined && { contractTier }),
    minMinutesBeforeBreak,
    minMinutesAfterBreak,
    shortestBreak,
    defaultHolidayRegionCode,
    properties: JSON.stringify(properties)
  };
};

// ---------------------------------------
// AdminSettingsGrid Component
// ---------------------------------------
export const AdminSettingsGrid: React.FC = () => {
  const [user, getEffectiveTenantId, role] = useAuthenticationService(s => [s.user, s.getEffectiveTenantId, s.role]);

  const superAdmin = Boolean(isAdmin(role) && user?.delegation?.isDelegating);

  const [
    {
      data: supportedHolidayRegionsData,
      error: supportedHolidayRegionsError,
      fetching: supportedHolidayRegionsFetching
    }
  ] = useGetSupportedHolidayRegionsQuery();
  if (supportedHolidayRegionsError) handleError(supportedHolidayRegionsError);

  const [snapshot, setSnapshot] = useState<AdminSettings>();
  const [rowData, setRowData] = useState<SettingsGridRow[]>([]);
  const [initialRowData, setInitialRowData] = useState<SettingsGridRow[]>([]);

  const apiUrl = useVariable('apiUrl')?.replace('/graphql', '');
  const tenantId = getEffectiveTenantId();
  const holidayRegionOptions = useMemo<OptionItem[]>(
    () =>
      (supportedHolidayRegionsData?.supportedHolidayRegions ?? [])
        .filter((region): region is NonNullable<typeof region> => region != null)
        .map(region => ({
          label: region.displayName,
          value: region.code
        })),
    [supportedHolidayRegionsData]
  );

  const isDirty = isRowDataDirty(rowData, initialRowData);

  const organisationSettingsReadUrl = `${apiUrl}/api/admin/organisation-settings`;
  const organisationSettingsWriteUrl = `${apiUrl}/${superAdmin ? 'api/superadmin' : 'api/admin'}/organisation-settings`;

  const createRequestInit = () => {
    const token = user?.access_token;
    if (!token || !tenantId) return null;

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'tenant-id': tenantId,
        'Content-Type': 'application/json'
      }
    };
  };

  useEffect(() => {
    const requestInit = createRequestInit();
    if (!requestInit) return;

    const load = async () => {
      const res = await fetch(organisationSettingsReadUrl, requestInit);
      const data = await res.json();
      const mapped = mapOrganisationSettingsToRowData(data, holidayRegionOptions, superAdmin);

      setSnapshot(data);
      setRowData(mapped);
      setInitialRowData(mapped.map(r => ({ ...r })));
    };

    load();
  }, [user, apiUrl, tenantId, holidayRegionOptions]);

  useEffect(() => {
    if (!snapshot || supportedHolidayRegionsFetching) return;

    const mapped = mapOrganisationSettingsToRowData(snapshot, holidayRegionOptions, superAdmin);
    setRowData(mapped);
    setInitialRowData(mapped.map(r => ({ ...r })));
  }, [snapshot, holidayRegionOptions, supportedHolidayRegionsFetching]);

  const handleSaveChanges = async () => {
    const requestInit = createRequestInit();
    if (!requestInit || !snapshot) return;

    const mapped = mapFromRowData(snapshot, rowData);

    await fetch(organisationSettingsWriteUrl, {
      method: 'PUT',
      ...requestInit,
      body: JSON.stringify(mapped)
    });

    setSnapshot(mapped);
    setInitialRowData(rowData.map(r => ({ ...r })));
  };

  return (
    <GenericSettingsGrid rowData={rowData} onRowDataChange={setRowData} onSave={handleSaveChanges} isDirty={isDirty} />
  );
};

export default AdminSettingsGrid;
